import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, KeyRound, Loader2 } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import {
  systemAccessTypesApi,
  systemAccessesApi,
  type AccessibleEmployee,
  type SystemAccessType,
  type EmployeeSystemAccess,
} from "@/features/finances/api/systemAccesses";

// ── Queries ───────────────────────────────────────────────────────────────────

function useVisibleEmployees() {
  return useQuery({
    queryKey: ["system-accesses-employees"],
    queryFn: systemAccessesApi.visibleEmployees,
  });
}

function useAccessTypes() {
  return useQuery({
    queryKey: ["system-access-types"],
    queryFn: systemAccessTypesApi.list,
  });
}

function useAccesses() {
  return useQuery({
    queryKey: ["system-accesses"],
    queryFn: systemAccessesApi.list,
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function userName(u: AccessibleEmployee) {
  return `${u.first_name} ${u.last_name}`.trim() || u.email;
}

const NO_BRANCH = "__none__";

type BranchGroup = { branchId: string; branchName: string; users: AccessibleEmployee[] };

/**
 * Same ordering as the employee list: deactivated accounts sink to the bottom
 * of their section, everything else is alphabetical by the displayed name.
 */
function sortEmployees(users: AccessibleEmployee[]): AccessibleEmployee[] {
  return [...users].sort((a, b) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    return userName(a).localeCompare(userName(b), "pl");
  });
}

/** Own branch first, the rest alphabetically, "Bez oddziału" always last. */
function groupByBranch(
  users: AccessibleEmployee[],
  ownBranchId?: string | null,
): BranchGroup[] {
  const map = new Map<string, BranchGroup>();
  for (const u of users) {
    const bid = u.branch?.id ?? NO_BRANCH;
    const bname = u.branch?.name ?? "Bez oddziału";
    if (!map.has(bid)) map.set(bid, { branchId: bid, branchName: bname, users: [] });
    map.get(bid)!.users.push(u);
  }
  return [...map.values()]
    .map((g) => ({ ...g, users: sortEmployees(g.users) }))
    .sort((a, b) => {
      if (a.branchId === NO_BRANCH) return 1;
      if (b.branchId === NO_BRANCH) return -1;
      const aOwn = ownBranchId && a.branchId === ownBranchId ? 0 : 1;
      const bOwn = ownBranchId && b.branchId === ownBranchId ? 0 : 1;
      if (aOwn !== bOwn) return aOwn - bOwn;
      return a.branchName.localeCompare(b.branchName, "pl");
    });
}

function extractError(err: unknown): string {
  const e = err as { response?: { data?: { errors?: unknown } } };
  const errors = e?.response?.data?.errors;
  if (!errors) return "Wystąpił błąd. Spróbuj ponownie.";
  if (typeof errors === "string") return errors;
  if (typeof errors === "object") {
    const vals = Object.values(errors as Record<string, unknown>).flat();
    return vals.join(" ");
  }
  return "Wystąpił błąd. Spróbuj ponownie.";
}

// ── Add Access Type Modal (global — adds to ALL employees) ────────────────────

function AddAccessTypeModal({
  onClose,
  onAdd,
  isPending,
  error,
}: {
  onClose: () => void;
  onAdd: (name: string, defaultChecked: boolean) => void;
  isPending: boolean;
  error: string | null;
}) {
  const [name, setName] = useState("");
  const [defaultChecked, setDefaultChecked] = useState(false);

  return (
    <Modal open title="Dodaj dostęp" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) onAdd(name.trim(), defaultChecked);
        }}
        className="flex flex-col gap-4 p-1"
      >
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
            Nazwa
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="np. GitHub, Jira…"
            autoFocus
            className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-primary)]"
            required
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={defaultChecked}
            onChange={(e) => setDefaultChecked(e.target.checked)}
            className="w-4 h-4 accent-[var(--color-primary)]"
          />
          <span className="text-sm text-[var(--color-text-primary)]">
            Domyślnie zaznaczony dla nowych użytkowników
          </span>
        </label>
        <p className="text-[11px] text-[var(--color-text-muted)]">
          Nowy typ pojawi się u wszystkich użytkowników jako niezaznaczony.
        </p>
        {error && (
          <p className="text-xs text-red-500 border border-red-200 bg-red-50 px-2 py-1.5">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-[var(--color-border)]">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            Anuluj
          </button>
          <button
            type="submit"
            disabled={isPending || !name.trim()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-wider bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {isPending && <Loader2 size={12} className="animate-spin" />}
            Dodaj
          </button>
        </div>
      </form>
    </Modal>
  );
}


// ── User Access Row ───────────────────────────────────────────────────────────

function UserAccessRow({
  user,
  accessTypes,
  entriesForUser,
  canEdit,
  onToggle,
  savingKeys,
}: {
  user: AccessibleEmployee;
  accessTypes: SystemAccessType[];
  entriesForUser: EmployeeSystemAccess[];
  canEdit: boolean;
  onToggle: (userId: string, typeId: string, entry: EmployeeSystemAccess | undefined) => void;
  savingKeys: Set<string>;
}) {
  const entryByTypeId = new Map(entriesForUser.map((e) => [e.access_type, e]));

  return (
    <tr className="border-b border-[var(--color-border-muted)] last:border-0 hover:bg-[var(--color-bg-elevated)] transition-colors">
      <td className="px-4 py-2.5 text-sm whitespace-nowrap w-64">
        <span
          className={
            user.is_active
              ? "text-[var(--color-text-primary)]"
              : "text-[var(--color-text-muted)]"
          }
        >
          {userName(user)}
        </span>
        {!user.is_active && (
          <Badge color="gray" className="ml-2">
            Nieaktywny
          </Badge>
        )}
      </td>
      <td className="px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-3">
          {accessTypes.map((type) => {
            const entry = entryByTypeId.get(type.id);
            const saveKey = entry?.id ?? `${user.id}_${type.id}`;
            const isSaving = savingKeys.has(saveKey);
            return (
              <label
                key={type.id}
                className="flex items-center gap-1.5 cursor-pointer select-none"
              >
                {isSaving ? (
                  <Loader2 size={14} className="animate-spin text-[var(--color-text-muted)]" />
                ) : (
                  <input
                    type="checkbox"
                    checked={entry?.is_checked ?? false}
                    onChange={() => canEdit && onToggle(user.id, type.id, entry)}
                    disabled={!canEdit}
                    className="w-4 h-4 accent-[var(--color-primary)] cursor-pointer disabled:cursor-default"
                  />
                )}
                <span className="text-xs text-[var(--color-text-secondary)]">{type.name}</span>
              </label>
            );
          })}
        </div>
      </td>
    </tr>
  );
}

// ── Branch Section (CEO view) ─────────────────────────────────────────────────

function BranchSection({
  branchName,
  users,
  accessTypes,
  entriesByEmployee,
  canEdit,
  onToggle,
  savingKeys,
}: {
  branchName: string;
  users: AccessibleEmployee[];
  accessTypes: SystemAccessType[];
  entriesByEmployee: Map<string, EmployeeSystemAccess[]>;
  canEdit: boolean;
  onToggle: (userId: string, typeId: string, entry: EmployeeSystemAccess | undefined) => void;
  savingKeys: Set<string>;
}) {
  return (
    <div>
      <div className="px-4 py-1.5 bg-[var(--color-bg-elevated)] border-b border-[var(--color-border)] sticky top-0 z-10">
        <span className="text-[11px] font-mono uppercase tracking-widest text-[var(--color-text-muted)] font-bold">
          {branchName}
        </span>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {users.map((u) => (
            <UserAccessRow
              key={u.id}
              user={u}
              accessTypes={accessTypes}
              entriesForUser={entriesByEmployee.get(u.id) ?? []}
              canEdit={canEdit}
              onToggle={onToggle}
              savingKeys={savingKeys}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function AccessesPage() {
  const currentUser = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [addTypeError, setAddTypeError] = useState<string | null>(null);
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());

  const { data: employees = [], isLoading: employeesLoading } = useVisibleEmployees();
  const { data: accessTypes = [], isLoading: typesLoading } = useAccessTypes();
  const { data: accesses = [], isLoading: accessesLoading } = useAccesses();

  const isLoading = employeesLoading || typesLoading || accessesLoading;
  const canEdit = currentUser?.role === "director" || !!currentUser?.is_ceo;
  const isCEO = !!currentUser?.is_ceo;

  const entriesByEmployee = new Map<string, EmployeeSystemAccess[]>();
  for (const entry of accesses) {
    const list = entriesByEmployee.get(entry.employee) ?? [];
    list.push(entry);
    entriesByEmployee.set(entry.employee, list);
  }

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["system-accesses"] });
    queryClient.invalidateQueries({ queryKey: ["system-access-types"] });
  };

  const { mutate: createType, isPending: isCreatingType } = useMutation({
    mutationFn: systemAccessTypesApi.create,
    onSuccess: () => {
      invalidate();
      setShowAddTypeModal(false);
      setAddTypeError(null);
    },
    onError: (err) => setAddTypeError(extractError(err)),
  });

  const handleToggle = async (
    userId: string,
    typeId: string,
    entry: EmployeeSystemAccess | undefined,
  ) => {
    const key = entry?.id ?? `${userId}_${typeId}`;
    setSavingKeys((s) => new Set(s).add(key));
    try {
      if (entry) {
        await systemAccessesApi.patch(entry.id, { is_checked: !entry.is_checked });
      } else {
        await systemAccessesApi.create({ employee: userId, access_type: typeId, is_checked: true });
      }
      queryClient.invalidateQueries({ queryKey: ["system-accesses"] });
    } finally {
      setSavingKeys((s) => {
        const n = new Set(s);
        n.delete(key);
        return n;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-[var(--color-text-muted)]">
        <Loader2 size={20} className="animate-spin mr-2" />
        <span className="text-sm font-mono">Ładowanie…</span>
      </div>
    );
  }

  const tableHeader = (
    <thead>
      <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
        <th className="px-4 py-2.5 text-left text-[11px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] w-64">
          Pracownik
        </th>
        <th className="px-4 py-2.5 text-left text-[11px] font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
          Dostępy
        </th>
      </tr>
    </thead>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound size={16} className="text-[var(--color-text-muted)]" />
          <h1 className="text-sm font-bold font-mono uppercase tracking-widest text-[var(--color-text-primary)]">
            Dostępy
          </h1>
          <span className="text-[11px] font-mono text-[var(--color-text-muted)] border border-[var(--color-border)] px-1.5 py-0.5">
            {employees.length}
          </span>
        </div>
        {canEdit && (
          <button
            onClick={() => { setAddTypeError(null); setShowAddTypeModal(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white transition-colors"
          >
            <Plus size={13} />
            Dodaj dostęp
          </button>
        )}
      </div>

      {/* CEO view: grouped by branch */}
      {isCEO ? (
        <div className="border border-[var(--color-border)] overflow-hidden divide-y divide-[var(--color-border)]">
          {employees.length === 0 ? (
            <p className="px-4 py-10 text-center text-xs font-mono text-[var(--color-text-muted)] uppercase tracking-wider">
              Brak użytkowników
            </p>
          ) : (
            groupByBranch(employees, currentUser?.branch?.id).map(({ branchId, branchName, users }) => (
              <BranchSection
                key={branchId}
                branchName={branchName}
                users={users}
                accessTypes={accessTypes}
                entriesByEmployee={entriesByEmployee}
                canEdit={canEdit}
                onToggle={handleToggle}
                savingKeys={savingKeys}
              />
            ))
          )}
        </div>
      ) : (
        /* Director/accountant/HR view: flat table */
        <div className="border border-[var(--color-border)] overflow-hidden">
          <table className="w-full text-sm">
            {tableHeader}
            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td
                    colSpan={2}
                    className="px-4 py-10 text-center text-xs font-mono text-[var(--color-text-muted)] uppercase tracking-wider"
                  >
                    Brak użytkowników w oddziale
                  </td>
                </tr>
              ) : (
                sortEmployees(employees).map((u) => (
                  <UserAccessRow
                    key={u.id}
                    user={u}
                    accessTypes={accessTypes}
                    entriesForUser={entriesByEmployee.get(u.id) ?? []}
                    canEdit={canEdit}
                    onToggle={handleToggle}
                    savingKeys={savingKeys}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showAddTypeModal && (
        <AddAccessTypeModal
          onClose={() => setShowAddTypeModal(false)}
          onAdd={(name, defaultChecked) =>
            createType({ name, default_checked: defaultChecked })
          }
          isPending={isCreatingType}
          error={addTypeError}
        />
      )}

    </div>
  );
}
